import React from 'react'
import {
  Datagrid,
  EditBase,
  type Identifier,
  type InputProps,
  ListBase,
  ListToolbar,
  Loading,
  Pagination,
  type RaRecord,
  type RowClickFunction,
  SaveButton,
  SimpleForm,
  TextField,
  TextInput,
  Toolbar,
  useListContext,
  useTranslate
} from 'react-admin'
import {
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@mui/material'
import { isEmpty, isNil, size } from 'lodash/fp'
import { useFormContext } from 'react-hook-form'
import log from 'loglevel'
import { SpotList } from 'components/SpotList'
import { type ConfigProps } from 'components/Dashboard'
import { type Partner } from '@mzc-watchover/adminapp-appsync'

log.setLevel((process.env['LOG_LEVEL'] as log.LogLevelDesc) ?? log.levels.DEBUG)

export const Mapping: React.FC<ConfigProps & { partner: Partial<Partner> }> = ({
  config,
  partner
}) => {
  const { Mapping: configMapping } = config
  const translate = useTranslate()
  const [mutate, setMutate] = React.useState(false)
  const [mode, setMode] = React.useState<string | undefined>(undefined)
  const [record, setRecord] = React.useState<string | undefined>(undefined)
  const MappingMutateFormContent: React.FC = () => {
    const [selectAccount, setSelectAccount] = React.useState<{
      managedBy: boolean
      ownedBy: boolean
    }>({ managedBy: false, ownedBy: false })
    const { getValues } = useFormContext()
    const handleClose =
      (source: string): (() => void) =>
      () => {
        const item = JSON.parse(JSON.stringify(selectAccount))
        item[source] = false
        setSelectAccount(item)
      }
    const SelectAccountInput: React.FC<InputProps> = (props) => {
      const { setValue, getValues } = useFormContext()
      const { source } = props
      log.info({ selectAccount: selectAccount[source] })
      const onClick = (): void => {
        const item = JSON.parse(JSON.stringify(selectAccount))
        item[source] = true
        setSelectAccount(item)
      }
      const handleRowClick: RowClickFunction = (
        _id: Identifier,
        _resource: string,
        record: RaRecord
      ): any => {
        setValue(source, record['name'], {
          shouldDirty: true,
          shouldValidate: true
        })
        const item = JSON.parse(JSON.stringify(selectAccount))
        item[source] = false
        setSelectAccount(item)
        return false
      }
      const MappingSelectAccountDialogContent: React.FC = () => {
        const { isLoading } = useListContext()
        if (isLoading) return <Loading />
        return (
          <Datagrid bulkActionButtons={false} rowClick={handleRowClick}>
            <TextField source="name" sx={{ display: 'block', width: 200 }} />
            <TextField source="email" sx={{ display: 'block', width: 300 }} />
          </Datagrid>
        )
      }
      const dialog = React.useMemo(() => {
        const onClick = (): void => {
          const item = JSON.parse(JSON.stringify(selectAccount))
          item[source] = false
          setSelectAccount(item)
        }
        const onClear = (): void => {
          setValue(source, '', {
            shouldDirty: true,
            shouldValidate: true
          })
          const item = JSON.parse(JSON.stringify(selectAccount))
          item[source] = false
          setSelectAccount(item)
        }
        return (
          <Dialog
            onClose={handleClose(source)}
            open={selectAccount[source]}
            maxWidth="md"
            fullWidth>
            <DialogTitle>
              {(source === 'managedBy'
                ? configMapping.selectAccountDialog
                : configMapping.selectOwnerAccountDialog) ??
                `${translate(
                  `resources.mappings.dialogs.${
                    source === 'managedBy'
                      ? 'selectAccount'
                      : 'selectOwnerAccount'
                  }.title`
                )}`}
            </DialogTitle>
            <DialogContent>
              <ListBase
                resource="accounts"
                filter={{ group: source === 'managedBy' ? 'manager' : 'owner' }}
                disableSyncWithLocation>
                <ListToolbar
                  filters={[<TextInput source="name" alwaysOn resettable />]}
                />
                <Card>
                  <MappingSelectAccountDialogContent />
                  <Pagination />
                </Card>
              </ListBase>
            </DialogContent>
            <DialogActions
              sx={{ justifyContent: 'flex-start', ml: '1em', mt: '-1em' }}>
              <Button variant="outlined" onClick={onClick}>
                {translate(
                  `resources.mappings.dialogs.selectAccount.buttons.cancel`
                )}
              </Button>
              <Button variant="contained" onClick={onClear}>
                {translate(
                  `resources.mappings.dialogs.selectAccount.buttons.clear`
                )}
              </Button>
            </DialogActions>
          </Dialog>
        )
      }, [handleClose, configMapping])
      const item = getValues()[source]
      return (
        <>
          <Typography>
            {configMapping.accounts ??
              translate(`resources.mappings.fields.${source}`)}
            {!isNil(item) && !isEmpty(item) && (
              <Typography variant="caption">
                (
                {translate(
                  `resources.mappings.fields.${source}.helperTextEdit`
                )}
                )
              </Typography>
            )}
          </Typography>
          <Chip
            onClick={onClick}
            label={
              !isNil(item) && !isEmpty(item)
                ? item
                : translate(`resources.mappings.fields.${source}.helperText`)
            }
          />
          {(selectAccount[source] as boolean) && dialog}
        </>
      )
    }
    const { code: codeField } = configMapping.fields
    const { spin } = getValues() as {
      spin: string
    }
    return (
      <>
        <TextInput source="name" fullWidth readOnly />
        <TextInput source="spin" fullWidth readOnly={size(spin) === 22} />
        <TextInput
          source={codeField.source}
          label={codeField.label}
          // readOnly={!isEmpty(getValues()[codeField.source])}
        />
        <SelectAccountInput source="managedBy" />
        <SelectAccountInput source="ownedBy" />
      </>
    )
  }
  const handleClose = (): void => {
    setMutate(false)
  }
  const handleRowClick: RowClickFunction = (
    id: Identifier,
    _resource: string,
    _record: RaRecord
  ) => {
    setMode('edit')
    setMutate(true)
    setRecord(id as string)
    return false
  }
  const MappingMutateToolbar: React.FC = () => {
    return (
      <Toolbar>
        <Button variant="outlined" onClick={handleClose} sx={{ mr: '1em' }}>
          {translate('resources.mappings.dialogs.mutate.buttons.cancel')}
        </Button>
        <SaveButton label="resources.mappings.dialogs.mutate.buttons.done" />
      </Toolbar>
    )
  }
  const { mutateDialog } = configMapping
  const dialog = React.useMemo(() => {
    return (
      <Dialog onClose={handleClose} open={mutate} maxWidth="md" fullWidth>
        <DialogTitle>
          {mode === 'edit'
            ? mutateDialog ??
              translate(`resources.mappings.dialogs.${mode as string}.title`)
            : ''}
        </DialogTitle>
        <DialogContent>
          {mode === 'edit' && !isNil(record) && (
            <EditBase
              id={record}
              resource="mappings"
              redirect={false}
              mutationMode="pessimistic"
              mutationOptions={{
                onSuccess: () => {
                  handleClose()
                }
              }}>
              <SimpleForm toolbar={<MappingMutateToolbar />}>
                <MappingMutateFormContent />
              </SimpleForm>
            </EditBase>
          )}
        </DialogContent>
      </Dialog>
    )
  }, [mutate, handleClose, mode, mutateDialog])
  // const { managedBy } = configMapping
  return (
    <>
      <SpotList
        resource="mappings"
        // items={[
        //   <FunctionField
        //     key="managedBy"
        //     label={managedBy ?? 'resources.mappings.fields.managedBy'}
        //     render={(record: { managedBy: { name: string; email: string } }) =>
        //       !isNil(record.managedBy)
        //         ? `${record.managedBy.name} (${record.managedBy.email})`
        //         : ''
        //     }
        //     sx={{ display: 'block', width: 400 }}
        //   />
        // ]}
        // additionalFilters={[
        //   <TextInput
        //     source="managedBy.name"
        //     label="resources.mappings.fields.managedBy"
        //     alwaysOn
        //     resettable
        //   />
        // ]}
        rowClick={handleRowClick}
        config={config}
        partner={partner}
      />
      {mutate && !isNil(mode) && dialog}
    </>
  )
}
